// Lista de pacientes con búsqueda y badge de alertas.

import { useEffect, useState } from 'react';
import { AlertTriangle, Search, User2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useModules } from '@/context/ModulesContext';
import { listPatients } from '../api';
import { cockpitLabels } from '../labels';
import type { PatientSummary } from '../types';
import { RecordDrawer } from './RecordDrawer';

export function PatientsView() {
  const { archetype } = useModules();
  const L = cockpitLabels(archetype);
  const [q, setQ] = useState('');
  const [patients, setPatients] = useState<PatientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // ?patient=<id> abre el expediente directo (lo usa Salud · DIF → «Expediente»).
  const [sp] = useSearchParams();
  const [activeId, setActiveId] = useState<string | null>(sp.get('patient'));

  // Debounce búsqueda
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      listPatients(q.trim(), 100)
        .then(setPatients)
        .catch((e) => setError(String(e?.message || e)))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={L.searchPlaceholder}
          className="w-full rounded border border-input bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
        />
      </div>

      {error && <div className="text-sm text-destructive">Error: {error}</div>}

      {loading ? (
        <div className="p-6 text-sm text-muted-foreground">Buscando…</div>
      ) : patients.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
          <User2 size={32} className="mx-auto mb-2 opacity-50" />
          <p>{q ? `Sin resultados para "${q}"` : L.emptyList}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">{L.entity}</th>
                <th className="px-4 py-2 font-medium">Edad/Sexo</th>
                <th className="px-4 py-2 font-medium">Alertas</th>
                <th className="px-4 py-2 font-medium">Última visita</th>
                <th className="px-4 py-2 text-right font-medium">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-foreground">
              {patients.map((p) => (
                <tr key={p.id} className="hover:bg-muted/20">
                  <td className="px-4 py-2">
                    <div className="font-medium">{p.display_name}</div>
                    {p.is_demo && (
                      <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase text-amber-400">demo</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {p.age !== null ? `${p.age}a` : '—'} · {p.sex || '?'}
                  </td>
                  <td className="px-4 py-2">
                    {p.has_alerts ? (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                        <AlertTriangle size={12} /> Banderas
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {p.last_encounter_at
                      ? new Date(p.last_encounter_at).toLocaleDateString('es-MX')
                      : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setActiveId(p.id)}
                      className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                    >
                      {L.openRecord}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeId && (
        <RecordDrawer patientId={activeId} onClose={() => setActiveId(null)} />
      )}
    </div>
  );
}
